import { FC, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";
import { SyncClientGroupSummarySchema } from "@/domains/openapi/types";
import { Uuid } from "@/domains/global/identifiers";
import { SyncClientGroupsTable } from "@/pages/sync-client-groups/shared/sync-client-groups-table";
import { Button } from "@radix-ui/themes";
import { DateTime } from "luxon";

export const SpaceAccountSyncClientGroupsPage: FC = () => {
  const navigate = useNavigate();
  const { spaceAccountId } = useParams<{ spaceAccountId: Uuid }>();

  if (!spaceAccountId) {
    return <div>Invalid ID</div>;
  }

  const pageSize = 1000;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["space-account-sync-client-groups", spaceAccountId],
    queryFn: () =>
      api.post("/internal/v1/sync-client-groups/search", {
        body: {
          filter_by_space_account_id: spaceAccountId,
          limit: pageSize,
        },
      }),
    staleTime: DateTime.fromObject({ minute: 1 }).toMillis(),
  });

  const handleViewSyncClientGroup = (id: string) => {
    navigate(atlasRoutes.syncClientGroupsView({ params: { syncClientGroupId: id } }).path);
  };

  const handleViewSyncCvr = (id: string) => {
    navigate(atlasRoutes.syncCvrsView({ params: { syncCvrId: id } }).path);
  };

  const handleViewSpaceAccount = () => {
    navigate(atlasRoutes.spaceAccountsView({ params: { spaceAccountId } }).path);
  };

  const tableData = useMemo<SyncClientGroupSummarySchema[]>(
    () => data?.data?.results ?? [],
    [data]
  );

  return (
    <Container>
      <h1>Sync Client Groups</h1>

      <Button variant="outline" onClick={() => handleViewSpaceAccount()}>
        View Space Account
      </Button>
      <br />
      <br />

      {/* Loading and Error States */}
      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching sync client groups: {error.message}</div>}

      {/* Table or No Results */}
      {tableData.length > 0 ? (
        <SyncClientGroupsTable
          data={tableData}
          onViewSyncClientGroup={handleViewSyncClientGroup}
          onViewSyncCvr={handleViewSyncCvr}
          pageSize={pageSize}
        />
      ) : (
        !isLoading && <div>No sync client groups found.</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;
